/**
 * Custom hook para obtener y manejar datos de cómics
 */
import { useState, useEffect, useCallback } from 'react';
import { getComics } from '../../core/api/api';
import type { Comic, UseComicsReturn } from '../types';

interface UseComicsOptions {
    fetchOnMount?: boolean;
}


/**
 * Hook para obtener la lista de cómics
 */
export const useComics = (options: UseComicsOptions = {}): UseComicsReturn & { refetch: () => Promise<Comic[]> } => {
    const { fetchOnMount = true } = options;

    const [comics, setComics] = useState<Comic[]>([]);
    const [loading, setLoading] = useState<boolean>(fetchOnMount);
    const [error, setError] = useState<string | null>(null);

    const fetchComics = useCallback(async (): Promise<Comic[]> => {
        setLoading(true);
        setError(null);

        try {
            const data = await getComics();

            // Procesar datos para añadir capítulos bloqueados
            const availableChapters = data.length;
            const lockedChapters: Comic[] = Array.from(
                { length: 9 - availableChapters },
                (_, i) => ({
                    id: `chapter-${availableChapters + i + 1}`,
                    title: {
                        español: `Capítulo ${availableChapters + i + 1}`,
                        ingles: `Chapter ${availableChapters + i + 1}`,
                    },
                    available: false,
                    images: { español: [], ingles: [] },
                })
            );

            const processedComics: Comic[] = [
                ...data.map((comic) => ({ ...comic, available: true })),
                ...lockedChapters,
            ];

            setComics(processedComics);
            return processedComics;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Error al cargar los cómics';
            setError(errorMessage);
            console.error('Error al obtener los cómics:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (fetchOnMount) {
            fetchComics();
        }
    }, [fetchOnMount, fetchComics]);

    return {
        comics,
        loading,
        error,
        refetch: fetchComics,
    };
};

export default useComics;
